import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

// Extend dayjs with plugins
dayjs.extend(utc);
dayjs.extend(timezone);

// Set default timezone to IST
dayjs.tz.setDefault('Asia/Kolkata');

/**
 * Convert UTC date to IST and format it
 * @param {string|Date} date - UTC date
 * @param {string} format - dayjs format string
 * @returns {string} - Formatted IST date
 */
export const formatToIST = (date, format = 'DD/MM/YYYY HH:mm:ss') => {
  if (!date) return 'N/A';
  return dayjs.utc(date).tz('Asia/Kolkata').format(format);
};

/**
 * Format date to IST with date only
 * @param {string|Date} date - UTC date
 * @returns {string} - Formatted IST date (DD/MM/YYYY)
 */
export const formatDateIST = (date) => {
  return formatToIST(date, 'DD/MM/YYYY');
};

/**
 * Format date to IST with time only
 * @param {string|Date} date - UTC date
 * @returns {string} - Formatted IST time (HH:mm:ss)
 */
export const formatTimeIST = (date) => {
  return formatToIST(date, 'HH:mm:ss');
};

/**
 * Format date to IST with short time
 * @param {string|Date} date - UTC date
 * @returns {string} - Formatted IST time (HH:mm)
 */
export const formatShortTimeIST = (date) => {
  return formatToIST(date, 'HH:mm');
};

/**
 * Format date to IST with 12-hour format
 * @param {string|Date} date - UTC date
 * @returns {string} - Formatted IST time (hh:mm A)
 */
export const formatTime12HourIST = (date) => {
  return formatToIST(date, 'hh:mm A');
};

/**
 * Get current IST time
 * @returns {dayjs.Dayjs} - Current IST time
 */
export const nowIST = () => {
  return dayjs().tz('Asia/Kolkata');
};

/**
 * Check if date is today in IST
 * @param {string|Date} date - UTC date
 * @returns {boolean} - True if date is today in IST
 */
export const isTodayIST = (date) => {
  if (!date) return false;
  const istDate = dayjs.utc(date).tz('Asia/Kolkata');
  const today = nowIST();
  return istDate.isSame(today, 'day');
};

/**
 * Format relative time in IST (e.g., "2 hours ago")
 * @param {string|Date} date - UTC date
 * @returns {string} - Relative time string
 */
export const formatRelativeTimeIST = (date) => {
  if (!date) return 'N/A';
  const istDate = dayjs.utc(date).tz('Asia/Kolkata');
  const now = nowIST();
  
  const diff = now.diff(istDate, 'minute');
  
  if (diff < 1) return 'Just now';
  if (diff < 60) return `${diff} min ago`;
  if (diff < 1440) return `${Math.floor(diff / 60)} hr ago`;
  if (diff < 10080) return `${Math.floor(diff / 1440)} days ago`;
  
  return formatToIST(date, 'DD/MM/YYYY');
};

/**
 * Format for transaction display
 * @param {string|Date} date - UTC date
 * @returns {object} - Object with date and time strings
 */
export const formatTransactionIST = (date) => {
  return {
    date: formatDateIST(date),
    time: formatTimeIST(date)
  };
};

/**
 * Format for order display
 * @param {string|Date} date - UTC date
 * @returns {object} - Object with date and time strings
 */
export const formatOrderIST = (date) => {
  return {
    date: formatDateIST(date),
    time: formatTimeIST(date)
  };
};
